/**
 * 이미지 URL 변환 유틸리티
 * 
 * 업로드된 이미지의 경로를 환경에 따라 적절한 URL로 변환하는 함수들을 제공합니다.
 * 개발 환경에서는 정적 파일 서빙을, 프로덕션 환경에서는 API 라우트를 통한 서빙을 지원합니다.
 */

/**
 * 이미지 경로를 안전한 URL로 변환하는 함수
 * 
 * 로컬 이미지 경로(/uploads/images/...)를 환경에 따라 적절한 URL로 변환합니다.
 * - 개발 모드: 정적 파일 경로 그대로 사용
 * - 프로덕션 모드: API 라우트를 통한 이미지 서빙
 * - Vercel Blob URL: 그대로 사용
 * 
 * @param imagePath - 변환할 이미지 경로
 * @returns 환경에 적합한 이미지 URL
 * 
 * @example
 * ```typescript
 * // 로컬 이미지 경로
 * const localPath = "/uploads/images/123_abc.jpg";
 * const safeUrl = getImageUrl(localPath);
 * // 개발: "/uploads/images/123_abc.jpg"
 * // 프로덕션: "/api/uploads/images/123_abc.jpg"
 * 
 * // Vercel Blob URL (변경 없음)
 * const blobUrl = "https://example.blob.vercel-storage.com/image.jpg";
 * const unchanged = getImageUrl(blobUrl);
 * // "https://example.blob.vercel-storage.com/image.jpg"
 * ```
 */
export function getImageUrl(imagePath: string): string {
  if (!imagePath) return '';

  // Vercel Blob URL이나 외부 URL인 경우 그대로 반환
  if (imagePath.startsWith('http')) {
    return imagePath;
  }

  // 로컬 업로드 이미지 경로인 경우
  if (imagePath.startsWith('/uploads/images/')) {
    // 클라이언트 사이드에서는 항상 API 라우트 사용
    if (typeof window !== 'undefined') {
      const filename = imagePath.split('/').pop();
      return `/api/uploads/images/${filename}`;
    }
    
    // 서버 사이드에서는 환경에 따라 분기
    const isDev = process.env.NODE_ENV === 'development';
    
    if (isDev) {
      // 개발 환경에서는 정적 파일 서빙 사용
      return imagePath;
    } else {
      // 프로덕션 환경에서는 API 라우트를 통한 서빙
      const filename = imagePath.split('/').pop();
      return `/api/uploads/images/${filename}`;
    }
  }

  // 그 외의 경우는 그대로 반환
  return imagePath;
}

/**
 * 팝업 이미지 URL을 안전하게 변환하는 함수
 * 
 * 팝업에서 사용되는 이미지 URL을 환경에 맞게 변환합니다.
 * getImageUrl의 팝업 전용 래퍼 함수입니다.
 * 
 * @param imageUrl - 팝업 이미지 URL
 * @returns 환경에 적합한 이미지 URL
 * 
 * @example
 * ```typescript
 * const popupImage = "/uploads/images/popup_123.jpg";
 * const safeUrl = getPopupImageUrl(popupImage);
 * ```
 */
export function getPopupImageUrl(imageUrl: string): string {
  return getImageUrl(imageUrl);
}

/**
 * 여러 이미지 URL을 일괄 변환하는 함수
 * 
 * 배열로 된 이미지 경로들을 모두 안전한 URL로 변환합니다.
 * 
 * @param imagePaths - 변환할 이미지 경로 배열
 * @returns 변환된 이미지 URL 배열
 * 
 * @example
 * ```typescript
 * const paths = [
 *   "/uploads/images/image1.jpg",
 *   "/uploads/images/image2.png"
 * ];
 * const urls = getBatchImageUrls(paths);
 * ```
 */
export function getBatchImageUrls(imagePaths: string[]): string[] {
  return imagePaths.map(path => getImageUrl(path));
}